'use client';

import { Alert, AlertBody, Drawer, Heading, TextPassage } from '@petsmart-ui/sparky';
import { useEffect, useId, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';

import { useCustomerAutoshipSubscriptions, useDecisions } from '@libs/common-next';

import { AutoshipInfoDrawer } from '../AutoshipInfoDrawer';
import { CartConfirmDrawer } from '../CartConfirmDrawer';
import { PDPStoreDrawer } from '../pdp-store-drawer';
import { SddAddressDrawer } from '../SddAddressDrawer';
import { isCustomerFirstAutoshipEligible } from './lib';
import { MiniPdpProductInfo } from './mini-pdp-product-info';
import { MiniPdpPurchaseForm } from './mini-pdp-purchase-form';
import { MiniPdpSkeleton } from './mini-pdp-skeleton';
import { useMiniPdpDrawer } from './model/use-mini-pdp-drawer';
import { useMiniPdpErrorAnalytics } from './model/use-mini-pdp-error-analytics';
import { useMiniPdpForm } from './model/use-mini-pdp-form';
import { useMiniPdpProduct } from './model/use-mini-pdp-product';
import type { MiniPdpDrawerProps } from './types';
import './mini-pdp-drawer.scss';

export function MiniPdpDrawer({ className }: MiniPdpDrawerProps) {
    const isOpen = useMiniPdpDrawer(state => state.isOpen);
    const close = useMiniPdpDrawer(state => state.close);
    const { base, tailored, isLoading, isError, isUnexpectedError } = useMiniPdpProduct();
    const defaultProduct = base?.variants[base.defaultSku];
    const purchaseTailored = isError ? null : tailored;
    const form = useMiniPdpForm(defaultProduct, base?.defaultSku);
    const [isSddAddressOpen, setIsSddAddressOpen] = useState(false);
    const selectedSku = useWatch({ control: form.control, name: 'selectedSku' });
    const resolvedSku = base?.variants[selectedSku] ? selectedSku : base?.defaultSku;
    const selectedProduct = resolvedSku && base ? base.variants[resolvedSku] : undefined;
    const [isStoreDrawerOpen, setIsStoreDrawerOpen] = useState(false);
    const [isAutoshipInfoOpen, setIsAutoshipInfoOpen] = useState(false);
    const isAutoshipEnabled = useDecisions('as_mini_pdp');
    const { autoshipData } = useCustomerAutoshipSubscriptions();
    const [isAddToCartError, setIsAddToCartError] = useState(false);
    const [isSddAddressError, setIsSddAddressError] = useState(false);
    const showError = isError || isAddToCartError;
    useMiniPdpErrorAnalytics(isUnexpectedError || isAddToCartError || isSddAddressError);

    const headingId = useId();

    // Don't render drawer on server or first client render to prevent Sparky animation issues
    // This avoids scroll lock, focus trap, and aria-modal activation on initial page load
    const [hasBeenRendered, setHasBeenRendered] = useState(false);

    useEffect(() => {
        setHasBeenRendered(true);
    }, []);

    if (!isOpen && isAutoshipInfoOpen) {
        setIsAutoshipInfoOpen(false);
    }

    const cartConfirmDrawer = <CartConfirmDrawer stacked forceClosed={!isOpen} onCloseAll={close} />;

    if (!hasBeenRendered) {
        return cartConfirmDrawer;
    }

    return (
        <FormProvider {...form}>
            <Drawer
                align="right"
                isActive={isOpen}
                onClose={() => !isSddAddressOpen && !isStoreDrawerOpen && !isAutoshipInfoOpen && close()}
                data-testid="mini-pdp-drawer"
                className={className ? `mini-pdp-drawer ${className}` : 'mini-pdp-drawer'}
                ariaLabelledBy={headingId}
                dismissible
            >
                <Drawer.Header className="mini-pdp-drawer__header">
                    <Heading id={headingId} tagName="h2" size="headline" data-testid="mini-pdp-drawer-title">
                        Choose Options
                    </Heading>
                </Drawer.Header>
                <Drawer.Body className="mini-pdp-drawer__body" data-testid="mini-pdp-drawer-body">
                    <div
                        className="mini-pdp-drawer__product"
                        data-testid="mini-pdp-product-data"
                        data-base-loaded={base ? 'true' : 'false'}
                        data-tailored-loaded={purchaseTailored ? 'true' : 'false'}
                    >
                        {showError && (
                            <Alert
                                variant="error"
                                iconName="exclamation-circle-fill"
                                className="mini-pdp-drawer__error"
                                data-testid="mini-pdp-error"
                                dismissible={false}
                            >
                                <AlertBody>
                                    <Heading tagName="h2" size="body-bold">
                                        Unexpected error has occurred
                                    </Heading>
                                    <TextPassage capLinelength={false}>Please refresh page to continue.</TextPassage>
                                </AlertBody>
                            </Alert>
                        )}
                        {(isLoading || showError) && !selectedProduct && <MiniPdpSkeleton />}
                        {selectedProduct && (
                            <div className="mini-pdp-drawer__options">
                                <MiniPdpProductInfo product={selectedProduct} />
                                <MiniPdpPurchaseForm
                                    base={base}
                                    product={selectedProduct}
                                    tailored={purchaseTailored}
                                    isLoading={isLoading}
                                    isError={isError}
                                    onErrorChange={setIsAddToCartError}
                                    onOpenSddAddress={() => setIsSddAddressOpen(true)}
                                    onOpenStoreDrawer={() => setIsStoreDrawerOpen(true)}
                                    onOpenAutoshipInfo={() => setIsAutoshipInfoOpen(true)}
                                />
                            </div>
                        )}
                    </div>
                </Drawer.Body>
            </Drawer>
            <SddAddressDrawer
                showBackButton
                showTrigger={false}
                isOpen={isSddAddressOpen}
                onOpenChange={setIsSddAddressOpen}
                onErrorChange={setIsSddAddressError}
                forceClosed={!isOpen}
                onCloseAll={close}
                isDeliveryAvailable={Boolean(
                    resolvedSku && tailored?.variants?.[resolvedSku]?.SDD?.doorDashValidationSuccess
                )}
                isLoading={isLoading}
                isError={isError}
            />
            <PDPStoreDrawer
                showBackButton
                forceClosed={!isOpen}
                onCloseAll={close}
                isActive={isStoreDrawerOpen}
                onOpenChange={setIsStoreDrawerOpen}
                sku={resolvedSku}
                drawerHeading="Pick up in store"
                toggleText="Only stores with item availability"
                className="pdp-page"
                minPurchaseQuantity={selectedProduct?.deliveryMethods?.BOPIS?.purchaseQuantities?.min}
                filterFn={resolvedSku ? stores => stores.filter(store => store.attributes?.bopisEnabled) : undefined}
            />
            {isAutoshipEnabled && (
                <AutoshipInfoDrawer
                    showBackButton
                    discountDisplay={selectedProduct?.autoship.discountDisplay}
                    isFirstAutoshipEligible={isCustomerFirstAutoshipEligible(autoshipData)}
                    isDrawerOpen={isAutoshipInfoOpen}
                    setIsDrawerOpen={setIsAutoshipInfoOpen}
                    onCloseAll={close}
                />
            )}
            {cartConfirmDrawer}
        </FormProvider>
    );
}
